import React, { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { X, MapPin, Building, ExternalLink, Linkedin, Globe } from 'lucide-react'
import { cn } from '../../utils/cn'
import { resolveTeamMemberModalSlots } from './teamMemberModalLogic.mjs'

const TITLE_ID = 'team-member-modal-title'
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'

const TeamMemberModal = ({ member, isOpen, onClose }) => {
  const modalRef = useRef(null)

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
  }

  const modalVariants = {
    hidden: {
      opacity: 0,
      scale: 0.8,
      y: 50
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        type: "spring",
        damping: 20,
        stiffness: 300
      }
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      y: 50,
      transition: {
        duration: 0.2
      }
    }
  }

  // Escape-to-close, body scroll lock, initial focus, and a manual focus
  // trap (WAI-ARIA dialog pattern) so Tab/Shift+Tab can't leave the dialog
  // while it's open. Focus is returned to the triggering team card by the
  // parent (LandingPage), which is the element that owns that reference.
  useEffect(() => {
    if (!isOpen) return undefined

    // Defer to the next tick so the modal has mounted before we move focus.
    const focusTimer = window.setTimeout(() => {
      modalRef.current?.focus()
    }, 0)

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose()
        return
      }

      if (e.key !== 'Tab' || !modalRef.current) return

      const focusable = modalRef.current.querySelectorAll(FOCUSABLE_SELECTOR)
      if (focusable.length === 0) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    document.body.style.overflow = 'hidden'

    return () => {
      window.clearTimeout(focusTimer)
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose])

  // Bail AFTER the hooks above, never before. This guard used to sit at the
  // very top of the component, which made the hook count depend on `member`:
  // with no selection React saw one hook (useRef), and the moment a team card
  // set a member it saw two (useRef + useEffect). React threw invariant #310
  // ("Rendered more hooks than during the previous render"), which unmounts
  // the whole tree — clicking a team member on the landing page blanked the
  // entire site to a white screen. Hooks must run on every render; only the
  // returned markup may be conditional.
  if (!member) return null

  // Which optional pieces of this member's profile have content — see
  // teamMemberModalLogic.mjs. Decides the role line, the affiliation
  // block, and whether to show the quiet "profile not yet available"
  // fallback when none of bio/expertise/education/contributions exist
  // (the migrations/0023 display-only case).
  const modalSlots = resolveTeamMemberModalSlots(member)

  // Portal to document.body — this modal used to render inline in
  // LandingPage's component tree, so its `fixed inset-0` box picked up
  // ancestor layout quirks (e.g. sibling-spacing margin) that shear the
  // backdrop short of the true viewport top. See components/ui/Modal.jsx
  // for the full root-cause writeup; same fix applied here since this
  // modal keeps its own bespoke animation/focus-trap rather than using
  // the shared <Modal> wrapper.
  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Modal */}
          <motion.div
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={TITLE_ID}
            tabIndex={-1}
            className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-3xl shadow-elev-3 focus:outline-none"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2"
              aria-label="Close modal"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>

            {/* Content */}
            <div className="p-8">
              {/* Header with photo and basic info */}
              <div className="flex flex-col md:flex-row items-center md:items-start gap-6 mb-8">
                {/* Photo */}
                <motion.div
                  className="relative w-32 h-32 rounded-2xl overflow-hidden ring-4 ring-primary-400/30 shadow-lg"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring" }}
                >
                  {member.image ? (
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover"
                      width={128}
                      height={128}
                      onError={(e) => {
                        e.target.style.display = 'none'
                        e.target.nextSibling.style.display = 'flex'
                      }}
                    />
                  ) : null}
                  <div
                    className="w-full h-full bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center"
                    style={{ display: member.image ? 'none' : 'flex' }}
                  >
                    <span className="text-primary-600 text-lg font-bold">
                      {member.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                </motion.div>

                {/* Basic info */}
                <div className="flex-1 text-center md:text-left">
                  <motion.h2
                    id={TITLE_ID}
                    className="text-3xl font-bold text-gray-800 mb-2"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    {member.name}
                  </motion.h2>

                  {/* Display-only profiles (migrations/0023 + --profile-only-order)
                      have no project role — display_role stays NULL rather than
                      guessed. Omit the element entirely, same reasoning as the
                      team-grid card (see teamMemberModalLogic.mjs). */}
                  {modalSlots.hasRole && (
                    <motion.div
                      className="flex items-center justify-center md:justify-start gap-2 text-primary-600 mb-3"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                    >
                      <Building className="w-4 h-4" />
                      <span className="font-medium">{member.role}</span>
                    </motion.div>
                  )}

                  {modalSlots.hasAffiliationBlock && (
                    <motion.div
                      className="flex items-center justify-center md:justify-start gap-3 mb-4"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.45 }}
                    >
                      {member.institutionLogo && (
                        <img
                          src={member.institutionLogo}
                          alt={member.affiliation || member.institutionShortName}
                          className="h-8 w-auto max-w-[140px] object-contain opacity-80 grayscale-[0.3] flex-shrink-0"
                        />
                      )}
                      {member.affiliation && (
                        <span className="text-sm text-gray-600">{member.affiliation}</span>
                      )}
                    </motion.div>
                  )}

                  {modalSlots.hasLocation && (
                    <motion.div
                      className="flex items-center justify-center md:justify-start gap-2 text-gray-600 mb-4"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 }}
                    >
                      <MapPin className="w-4 h-4" />
                      <span>{member.location}</span>
                    </motion.div>
                  )}

                  {/* Social links */}
                  {(modalSlots.hasLinkedin || modalSlots.hasResearchProfile) && (
                    <motion.div
                      className="flex gap-3 justify-center md:justify-start"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.6 }}
                    >
                      {modalSlots.hasLinkedin && (
                        <a
                          href={member.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`${member.name} on LinkedIn`}
                          className="p-2 rounded-lg bg-primary-100 hover:bg-primary-200 text-primary-600 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2"
                        >
                          <Linkedin className="w-4 h-4" />
                        </a>
                      )}
                      {modalSlots.hasResearchProfile && (
                        <a
                          href={member.researchProfile}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`${member.name} research profile`}
                          className="p-2 rounded-lg bg-teal-100 hover:bg-teal-200 text-teal-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:ring-offset-2"
                        >
                          <Globe className="w-4 h-4" />
                        </a>
                      )}
                    </motion.div>
                  )}
                </div>
              </div>

              {/* Bio section */}
              {modalSlots.hasBio && (
                <motion.div
                  className="mb-6"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                >
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">Biography</h3>
                  <p className="text-gray-600 leading-relaxed">{member.bio}</p>
                </motion.div>
              )}

              {/* Expertise */}
              {modalSlots.hasExpertise && (
                <motion.div
                  className="mb-6"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                >
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">Areas of Expertise</h3>
                  <div className="flex flex-wrap gap-2">
                    {member.expertise.map((skill, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm font-medium"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Education */}
              {modalSlots.hasEducation && (
                <motion.div
                  className="mb-6"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.9 }}
                >
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">Education</h3>
                  <div className="space-y-2">
                    {member.education.map((edu, index) => (
                      <div key={index} className="text-gray-600">
                        <span className="font-medium">{edu.degree}</span>
                        {edu.institution && <span className="text-gray-500"> - {edu.institution}</span>}
                        {edu.year && <span className="text-gray-400"> ({edu.year})</span>}
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Key contributions */}
              {modalSlots.hasContributions && (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.0 }}
                >
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">Key Contributions to IEWRS</h3>
                  <ul className="space-y-2">
                    {member.contributions.map((contribution, index) => (
                      <li key={index} className="flex items-start gap-2 text-gray-600">
                        <span className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0" />
                        <span>{contribution}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              )}

              {/* No bio/expertise/education/contributions at all — this is the
                  migrations/0023 display-only case (a photo + a name, nothing
                  else was in the source material). A header floating above
                  dead space reads as broken; a quiet, honest, muted line reads
                  as deliberate. Never fabricated content, never an alarming
                  empty-state — just the fact of the matter, in the product's
                  existing muted-text style (matches the empty/placeholder
                  copy used elsewhere, e.g. the "Photo" placeholder above). */}
              {modalSlots.showIncompleteNotice && (
                <motion.p
                  className="text-sm text-gray-500 text-center md:text-left"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                >
                  Full profile not yet available.
                </motion.p>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  )
}

export default TeamMemberModal
